import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
} from '@angular/core';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { ExportApi } from '../../../../shared/services/api/export.api';
import { ExportType } from '../../../../shared/enums/export-type.enum';
import { catchError, EMPTY, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpUtils } from '../../../../shared/utils/http.utils';
import { MessageService } from 'primeng/api';
import { VisualizationApi } from '../../../../shared/services/api/visualization.api';
import { VisualizationType } from '../../../../shared/enums/visualization-type.enum';
import { ApplicationVisualizationDialog } from '../application-visualization-dialog/application-visualization-dialog';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';

@Component({
  standalone: true,
  templateUrl: './poswiadczenie-status.component.html',
  styleUrls: ['./poswiadczenie-status.component.scss'],
  imports: [ButtonModule],
  providers: [ExportApi, MessageService, DialogService, VisualizationApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoswiadczenieStatusComponent {
  readonly #config = inject(DynamicDialogConfig);
  readonly #dialogRef = inject(DynamicDialogRef);
  readonly #exportApi = inject(ExportApi);
  readonly #destroyRef = inject(DestroyRef);
  readonly #visualizationApi = inject(VisualizationApi);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #dialogService = inject(DialogService);

  readonly #poswiadczenie = this.#config.data?.poswiadczenie;
  readonly #documentId = this.#config.data?.documentId as number | undefined;

  protected readonly upoMissingText = computed<string | null>(() => {
    const poswiadczenie = this.#poswiadczenie;
    if (!poswiadczenie || !poswiadczenie.dataUtworzenia) {
      return 'Jednostka adresata wniosku nie przesłała jeszcze Urzędowego Poświadczenia Odbioru';
    }
    return null;
  });

  protected readonly headerText = computed<string | null>(() =>
    this.#buildHeaderText(),
  );

  #buildHeaderText(): string | null {
    const poswiadczenie = this.#poswiadczenie;
    if (!poswiadczenie || !poswiadczenie.dataUtworzenia) return null;

    const date = new Date(poswiadczenie.dataUtworzenia);

    const day = date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const time = date.toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `Dnia ${day} o godzinie ${time} z jednostki ${poswiadczenie.adresat} otrzymano Urzędowe Poświadczenie Odbioru.`;
  }

  protected visualizeUpo(): void {
    const documentId = this.#documentId;
    if (!documentId) return;

    this.#visualizationApi
      .getVisualization$(documentId, VisualizationType.UPO)
      .pipe(
        tap((content: string) => this.#openVisualizationDialog(content)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Błąd podczas wyświetlenia poświadczenia',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #openVisualizationDialog(content: string): void {
    this.#dialogService.open(ApplicationVisualizationDialog, {
      closable: true,
      header: `Wizualizacja poświadczenia`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: { content },
      style: {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        margin: '0',
      },
    });
  }

  protected exportUpo(): void {
    const poswiadczenie = this.#poswiadczenie;
    if (!poswiadczenie || !poswiadczenie.id) return;

    this.#exportApi
      .exportApplication$(poswiadczenie.id, ExportType.UPO)
      .pipe(
        tap((resp) => HttpUtils.downloadFile(resp)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Błąd podczas pobierania poświadczenia',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected hidePoswiadczenieStatusDialog(): void {
    this.#dialogRef.close();
  }
}
